import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import Auth from "./Auth";
import { useDarkMode } from "../App";
import styles from "./AuthModal.module.css";

type Props = {
  open: boolean;
  onClose: () => void;
  initialMode: "signin" | "signup";
  setToken: (token: string) => void;
};

export default function AuthModal({ open, onClose, initialMode, setToken }: Props) {
  const { darkMode } = useDarkMode();
  // Keep a local mode so the modal header stays in sync when the user
  // toggles Sign In ↔ Sign Up inside the embedded form.
  const [mode, setMode] = useState<"signin" | "signup">(initialMode);

  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="auth-modal-title"
      className={styles.dialogRoot}
      BackdropProps={{
        className: styles.backdrop,
      }}
      PaperProps={{
        className: `${styles.paper} ${darkMode ? styles.paperDark : ""}`,
        elevation: 0,
      }}
    >
      <div className={styles.shell}>
        <div className={styles.glow} aria-hidden />

        <header className={styles.header}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden>
              <DirectionsCarIcon fontSize="small" />
            </span>
            <div>
              <p className={styles.brandName}>Drive Now!</p>
              <p className={styles.brandHint}>
                {mode === "signin" ? "Welcome back" : "Create your free account"}
              </p>
            </div>
          </div>

          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            className={styles.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </header>

        <DialogContent className={styles.content}>
          <h2 id="auth-modal-title" className={styles.srOnly}>
            {mode === "signin" ? "Sign In" : "Sign Up"}
          </h2>
          <Auth
            variant="embedded"
            initialMode={mode}
            setToken={setToken}
            onModeChange={setMode}
          />
        </DialogContent>
      </div>
    </Dialog>
  );
}
